import { useState } from 'react'
import { safeStorage } from '../lib/safeStorage'
import { setManualStatusOverride } from '../lib/episode'
import { revivalInfo } from '../lib/revival'

// ── The Return ────────────────────────────────────────────────────────────────
// Revival detection: you rolled credits on this show, and now there are NEW
// episodes beyond the point where you finished (a renewed season, a revival,
// or an "ended" show that came back). Nothing in the app noticed this before —
// your keepsake said The End while S3 quietly aired.
//
// Detection (cheap, all data already on the show object):
//   status === 'completed' AND
//   (an upcoming episode exists, OR the last-aired episode is beyond either
//    your completion date or your saved progress marker).
// One-tap resume: status → watching, progress → first ep of the new season.
// Dismissible per show+season (localStorage) so it never nags.

const dismissKey = (showId, season) => `nextep_revival_dismissed_${showId}_s${season}`

// The rule itself lives in lib/revival (pure) so the deck queue and Tonight share it.
export { revivalInfo }

export function RevivalBanner({ show, wl }) {
  const status      = wl?.statuses?.[show?.id]
  const info        = revivalInfo(show, status, wl?.completedAt?.[show?.id], wl?.progress?.[show?.id])
  const [hidden, setHidden]   = useState(() => info ? safeStorage.get(dismissKey(show.id, info.season)) === '1' : false)
  const [resumed, setResumed] = useState(false)

  if (!info || hidden || safeStorage.get(dismissKey(show.id, info.season)) === '1') return null

  const resume = () => {
    setResumed(true)
    // If the new season hasn't aired yet, ShowDetail's caughtUpFlip reconcile effect would
    // otherwise immediately re-derive 'caught_up' off the same air data and flip this right
    // back — the Resume tap looked like a no-op (Jason 2026-08-02). Same guard the status
    // sheet uses, keyed to the exact progress this resume lands on.
    const newProgress = { season: info.season, episode: 1 }
    setManualStatusOverride(safeStorage, show.id, 'watching', newProgress)
    wl.setStatus(show.id, 'watching')
    // Land on the first episode of the new season — not mid-season wherever
    // the network happens to be. watchedEp: null → no ghost activity row.
    wl.saveProgress(show.id, newProgress, { watchedEp: null })
    window.dispatchEvent(new CustomEvent('nextep:milestone', { detail: {
      title: 'The story continues 🎬',
      body:  `${show.name}, back in your Watching list at S${info.season}·E1`,
      icon:  '📡',
      route: `/show/${show.id}`,
    }}))
    setTimeout(() => setHidden(true), 600)
  }

  const dismiss = () => {
    safeStorage.set(dismissKey(show.id, info.season), '1')
    setHidden(true)
  }

  return (
    <div style={{
      marginTop:8, padding:'13px 14px', borderRadius:'var(--radius-md)', position:'relative', overflow:'hidden',
      background:'linear-gradient(135deg, color-mix(in srgb, var(--accent) 16%, transparent), rgba(var(--amber-rgb),0.10))',
      border:'1px solid color-mix(in srgb, var(--accent) 40%, transparent)',
      fontFamily:'var(--font)',
      '--nxa':'fadeIn 0.5s ease',
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:11 }}>
        <span style={{ fontSize:24, flexShrink:0, filter:'drop-shadow(0 0 8px color-mix(in srgb, var(--accent) 70%, transparent))' }}>📡</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ color:'var(--purple)', fontWeight:800, fontSize:10, letterSpacing:'0.18em', marginBottom:2 }}>THE RETURN</div>
          <div style={{ color:'var(--text-strong)', fontWeight:700, fontSize:14, lineHeight:1.25 }}>
            {show.name} is back, Season {info.season}
          </div>
          <div style={{ color:'var(--text-dim)', fontSize:11.5, marginTop:2 }}>
            {info.upcoming && info.airDate
              ? `New episodes from ${info.airDate}. You rolled credits, the story didn't.`
              : 'New episodes have aired since you rolled credits.'}
          </div>
        </div>
      </div>
      <div style={{ display:'flex', gap:8, marginTop:11 }}>
        <button onClick={resume} disabled={resumed} style={{
          flex:2, minHeight:44, background:'var(--amber)', border:'none',
          borderRadius:'var(--radius-pill)', color:'var(--amber-ink)', fontWeight:700, fontSize:13, padding:'10px 8px',
          cursor:'pointer', fontFamily:'var(--font)',
        }}>{resumed ? 'Welcome back 🎬' : `Resume, S${info.season}·E1 →`}</button>
        <button onClick={dismiss} style={{
          flex:1, minHeight:44, background:'none', border:'1px solid var(--border)', borderRadius:'var(--radius-pill)',
          color:'var(--text-dim)', fontWeight:600, fontSize:12, padding:'10px 8px',
          cursor:'pointer', fontFamily:'var(--font)',
        }}>I'm done</button>
      </div>
    </div>
  )
}
